import { EmptyPage } from '@components/EmptyPage';
import { useAppbarTitle } from '@hooks/useAppbarTitle';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { usePageTitle } from '@hooks/usePageTitle';
import { mdiArrowLeft } from '@mdi/js';
import Icon from '@mdi/react';
import {
	Box,
	Button,
	CircularProgress,
	Container,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

// Status options - using seasonal statuses
export type OfferStatus = 'ongoing' | 'completed' | 'archived';

export interface FarmerOffersPageProps {
	// Page configuration
	pageTitle: string;

	// Store state and actions
	requests: any[];
	isRequestsLoading: boolean;
	requestsError: string | null;
	selectedRequestId: string | null;
	offers: any[];
	isOffersLoading: boolean;
	offersError: string | null;
	setSelectedRequestId: (id: string | null) => void;
	setRequests: (requests: any[]) => void;
	setIsRequestsLoading: (loading: boolean) => void;
	setRequestsError: (error: string | null) => void;
	setOffers?: (offers: any[]) => void;
	setIsOffersLoading?: (loading: boolean) => void;
	setOffersError?: (error: string | null) => void;
	resetState?: () => void;

	// Service functions
	getRequests: (status: OfferStatus) => Promise<any>;
	getOffersForRequest?: (requestId: string, serviceId?: string) => Promise<any>;

	// Components
	RequestsList: React.ComponentType<any>;
	OffersList: React.ComponentType<any>;

	// Configuration
	hasOffersPanel?: boolean;
	defaultStatus?: OfferStatus;
	onStatusChange?: (status: string) => void;
}

// Section loading component
const SectionLoader = () => {
	return (
		<Box
			sx={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				height: '50vh',
				width: '100%',
			}}
		>
			<CircularProgress size={50} sx={{ color: '#3da08f' }} />
		</Box>
	);
};

export const FarmerOffersPage: React.FC<FarmerOffersPageProps> = ({
	pageTitle,
	requests,
	isRequestsLoading,
	requestsError,
	selectedRequestId,
	offers,
	isOffersLoading,
	offersError,
	setSelectedRequestId,
	setRequests,
	setIsRequestsLoading,
	setRequestsError,
	setOffers,
	setIsOffersLoading,
	setOffersError,
	resetState,
	getRequests,
	getOffersForRequest,
	RequestsList,
	OffersList,
	hasOffersPanel = false,
	defaultStatus = 'ongoing',
	onStatusChange,
}) => {
	const { t } = useTranslation();
	const { navigateWithLanguage } = useLanguageNavigate();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const [statusFilter, setStatusFilter] = useState<OfferStatus>(defaultStatus);
	
	// Track loading state locally to avoid issues with prop updates
	const [isContentLoading, setIsContentLoading] = useState(true);
	const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const initialRenderRef = useRef(true);
	const apiCallInProgressRef = useRef(false);

	// Set page title
	usePageTitle(pageTitle);
	useAppbarTitle('requestsOffers');

	// Handle status filter change
	const handleStatusFilterChange = useCallback(
		(event: SelectChangeEvent) => {
			// Prevent changes during loading
			if (isRequestsLoading) return;

			const newStatus = event.target.value as OfferStatus;
			console.log(`[FarmerOffersPage] Status filter changed to ${newStatus}`);
			setStatusFilter(newStatus);
			
			// Call onStatusChange if provided
			if (onStatusChange) {
				onStatusChange(newStatus);
			}
			
			// Clear current selection and offers immediately for better UX
			setSelectedRequestId(null);
			if (setOffers) setOffers([]);
			setRequestsError(null);
			if (setOffersError) setOffersError(null);
			
			// Show loading state when changing filters
			setIsContentLoading(true);
		},
		[isRequestsLoading, setSelectedRequestId, setOffers, setRequestsError, setOffersError, onStatusChange]
	);

	// Fetch requests based on current filter
	useEffect(() => {
		const fetchRequests = async () => {
			try {
				console.log(`[FarmerOffersPage] Fetching ${statusFilter} requests...`);
				setIsRequestsLoading(true);
				setRequestsError(null);
				setIsContentLoading(true);
				apiCallInProgressRef.current = true;

				const response = await getRequests(statusFilter);
				const requests = response.data.requests;
				console.log(`[FarmerOffersPage] Received ${requests.length} requests`);

				setRequests(requests);

				// Auto-select first request if available
				if (requests.length > 0) {
					// If no request is selected or the selected request is not in the new list, select the first one
					const currentRequestExists =
						selectedRequestId &&
						requests.some((req: any) => (req.request_id || req.id) === selectedRequestId);
					if (!currentRequestExists) {
						const firstRequestId = requests[0].request_id || requests[0].id;
						console.log(`[FarmerOffersPage] Auto-selecting first request: ${firstRequestId}`);
						setSelectedRequestId(firstRequestId);
					}
				} else {
					// No requests available, clear selection
					console.log(`[FarmerOffersPage] No requests available, clearing selection`);
					setSelectedRequestId(null);
					if (setOffers) setOffers([]);
					if (setOffersError) setOffersError(null);
					
					// If no requests, we can stop loading immediately
					setIsContentLoading(false);
					apiCallInProgressRef.current = false;
				}
			} catch (error) {
				console.error('[FarmerOffersPage] Error fetching requests:', error);
				setRequestsError(error instanceof Error ? error.message : 'Error fetching requests');
				setRequests([]);
				setSelectedRequestId(null);
				if (setOffers) setOffers([]);
				if (setOffersError) setOffersError(null);
				
				// On error, stop loading
				setIsContentLoading(false);
				apiCallInProgressRef.current = false;
			} finally {
				setIsRequestsLoading(false);
				
				// If no offers panel or mobile, we can complete loading here
				if (!hasOffersPanel || isMobile) {
					// Add a small delay to ensure smooth transition
					setTimeout(() => {
						setIsContentLoading(false);
						apiCallInProgressRef.current = false;
					}, 300);
				}
			}
		};

		fetchRequests();
	}, [
		statusFilter,
		setRequests,
		setSelectedRequestId,
		setIsRequestsLoading,
		setRequestsError,
		setOffers,
		setOffersError,
		getRequests,
		selectedRequestId,
		hasOffersPanel,
		isMobile,
	]);

	// Fetch offers when selectedRequestId changes (for pages with offers panel)
	useEffect(() => {
		const fetchOffers = async () => {
			if (!selectedRequestId || isMobile || !hasOffersPanel || !getOffersForRequest) {
				return;
			}

			try {
				console.log(`[FarmerOffersPage] Fetching offers for request ${selectedRequestId}...`);
				if (setIsOffersLoading) setIsOffersLoading(true);
				if (setOffersError) setOffersError(null);
				apiCallInProgressRef.current = true;

				// Get the selected request to extract service_id
				const selectedRequest = requests.find(req => (req.request_id || req.id) === selectedRequestId);
				const serviceId = selectedRequest?.services_id || selectedRequest?.service_id || '';

				const response = await getOffersForRequest(selectedRequestId, serviceId);
				console.log(`[FarmerOffersPage] Received ${response.data.offers.length} offers`);
				if (setOffers) setOffers(response.data.offers);
			} catch (error) {
				console.error('[FarmerOffersPage] Error fetching offers for selected request:', error);
				if (setOffersError)
					setOffersError(error instanceof Error ? error.message : 'Error fetching offers');
				if (setOffers) setOffers([]);
			} finally {
				if (setIsOffersLoading) setIsOffersLoading(false);
				
				// Add a small delay to ensure smooth transition
				setTimeout(() => {
					setIsContentLoading(false);
					apiCallInProgressRef.current = false;
				}, 300);
			}
		};

		fetchOffers();
	}, [
		selectedRequestId,
		isMobile,
		hasOffersPanel,
		getOffersForRequest,
		setIsOffersLoading,
		setOffersError,
		setOffers,
		requests,
	]);
	
	// Effect to handle loading state based on API loading states
	useEffect(() => {
		// If API is loading, ensure content loading is shown
		if (isRequestsLoading || isOffersLoading) {
			if (!isContentLoading) {
				console.log('[FarmerOffersPage] API loading detected, showing loader');
				setIsContentLoading(true);
			}
		}
		
		return () => {
			if (loadingTimeoutRef.current) {
				clearTimeout(loadingTimeoutRef.current);
			}
		};
	}, [isRequestsLoading, isOffersLoading, isContentLoading]);

	// Handle selection of a request
	const handleSelectRequest = useCallback(
		(requestId: string) => {
			// For desktop view, always update the selected request ID
			if (!isMobile) {
				console.log(`[FarmerOffersPage] Request selected: ${requestId}`);
				setSelectedRequestId(requestId);
				// Show loading when selecting a new request (for offers panel)
				if (hasOffersPanel) {
					setIsContentLoading(true);
				}
				return;
			}

			// For mobile, we don't change the global selectedRequestId for pages with offers panel
			// The RequestsList component handles collapsing/expanding internally
			// This way mobile can have multiple expanded requests
		},
		[isMobile, setSelectedRequestId, hasOffersPanel]
	);

	// Clean up on unmount
	useEffect(() => {
		return () => {
			console.log('[FarmerOffersPage] Component unmounting, cleaning up');
			resetState?.();
			if (loadingTimeoutRef.current) {
				clearTimeout(loadingTimeoutRef.current);
			}
		};
	}, [resetState]);

	// Initialize component on mount - force loading state
	useEffect(() => {
		console.log('[FarmerOffersPage] Component mounted, initializing');
		// Force loading state to true on mount
		setIsContentLoading(true);
		initialRenderRef.current = true;
		
		return () => {
			initialRenderRef.current = false;
		};
	}, []);

	const handleGoBack = () => {
		navigateWithLanguage('/farmer/view-offers');
	};

	const isEmpty = requests.length === 0 && !isRequestsLoading;
	
	// Header section is always visible
	const headerSection = (
		<Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
			<Button
				className="w-[120px]"
				onClick={handleGoBack}
				variant="text"
				sx={{ backgroundColor: 'white', color: 'black' }}
				startIcon={<Icon path={mdiArrowLeft} size={1} />}
			>
				{t('back')}
			</Button>

			{/* Status Filter */}
			<FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
				<InputLabel id="status-select-label">{t('status')}</InputLabel>
				<Select
					labelId="status-select-label"
					id="status-select"
					value={statusFilter}
					onChange={handleStatusFilterChange}
					label={t('status')}
					disabled={isRequestsLoading || apiCallInProgressRef.current}
					startAdornment={isRequestsLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}
				>
					<MenuItem value="ongoing">{t('ongoing', 'Ongoing')}</MenuItem>
					<MenuItem value="completed">{t('completed', 'Completed')}</MenuItem>
					<MenuItem value="archived">{t('archived', 'Archived')}</MenuItem>
				</Select>
			</FormControl>
		</Box>
	);

	console.log(`[FarmerOffersPage] Rendering content: isEmpty=${isEmpty}, requests=${requests.length}, offers=${offers.length}, isLoading=${isContentLoading}, apiCallInProgress=${apiCallInProgressRef.current}`);

	return (
		<Container maxWidth="xl" sx={{ py: 4 }}>
			{headerSection}

			{/* Show loader when loading */}
			{isContentLoading && <SectionLoader />}

			{/* Show content when not loading */}
			{!isContentLoading && (
				<>
					{isEmpty ? (
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								minHeight: '50vh',
							}}
						>
							<EmptyPage
								title={
									statusFilter === 'completed'
										? t('noCompletedOffersYet')
										: statusFilter === 'archived'
											? t('noArchivedOffersYet')
											: t('noRequests')
								}
								buttonText={
									statusFilter === 'completed' || statusFilter === 'archived'
										? t('viewCurrent')
										: t('goToServices')
								}
								onButtonClick={() => {
									statusFilter === 'completed' || statusFilter === 'archived'
										? setStatusFilter('ongoing')
										: navigateWithLanguage('/farmer/services');
								}}
							/>
						</Box>
					) : (
						<>
							{isMobile ? (
								// Mobile layout: Single column with collapsible offers
								<Box
									sx={{
										p: 2,
										bgcolor: 'background.paper',
										borderRadius: '8px',
										boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
									}}
								>
									<RequestsList
										requests={requests}
										selectedRequestId={selectedRequestId}
										isLoading={isRequestsLoading}
										error={requestsError}
										onSelectRequest={handleSelectRequest}
										offers={offers}
										isOffersLoading={isOffersLoading}
										offersError={offersError}
										isMobile={isMobile}
										requestStatus={statusFilter}
									/>
								</Box>
							) : (
								// Desktop layout: Two columns side by side
								<Grid container spacing={3}>
									{/* Requests Panel (Left Side) */}
									<Grid item xs={12} md={hasOffersPanel ? 6 : 12} lg={hasOffersPanel ? 6 : 12}>
										<Box
											sx={{
												p: 2,
												bgcolor: 'background.paper',
												borderRadius: '8px',
												boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
												height: '100%',
												minHeight: '70vh',
												maxHeight: '80vh',
												overflow: 'auto',
											}}
										>
											<Typography variant="h5" sx={{ mb: 3, fontWeight: 500 }}>
												{t('requests', 'Requests')}
											</Typography>

											<RequestsList
												requests={requests}
												selectedRequestId={selectedRequestId}
												isLoading={isRequestsLoading}
												error={requestsError}
												onSelectRequest={handleSelectRequest}
												offers={offers}
												isOffersLoading={isOffersLoading}
												offersError={offersError}
												isMobile={isMobile}
												requestStatus={statusFilter}
											/>
										</Box>
									</Grid>

									{/* Offers Panel (Right Side) - Only if hasOffersPanel is true */}
									{hasOffersPanel && (
										<Grid item xs={12} md={6} lg={6}>
											<Box
												sx={{
													p: 2,
													bgcolor: 'background.paper',
													borderRadius: '8px',
													boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
													height: '100%',
													minHeight: '70vh',
													maxHeight: '80vh',
													overflow: 'auto',
												}}
											>
												<OffersList
													offers={offers}
													isLoading={isOffersLoading}
													error={offersError}
													requestId={selectedRequestId}
													userId={
														requests.find((r) => (r.request_id || r.id) === selectedRequestId)
															?.specialist_id || ''
													}
													requestStatus={statusFilter}
													requestStartDate={
														requests.find((r) => (r.request_id || r.id) === selectedRequestId)
															?.start_date
													}
													requestEndDate={
														requests.find((r) => (r.request_id || r.id) === selectedRequestId)
															?.end_date
													}
												/>
											</Box>
										</Grid>
									)}
								</Grid>
							)}
						</>
					)}
				</>
			)}
		</Container>
	);
};
